import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  type PropsWithChildren,
  useMemo,
} from 'react';
import axios from 'utils/axios';
import { type FormData, type Account } from '@types';

interface Context {
  token: string | null;
  account: Account | null;
  isLoggedIn: boolean;
  register: (payload: FormData) => Promise<any>;
  login: (username: string, password: string) => Promise<any>;
  loginWithGoogle: (credential: string) => Promise<any>;
  logout: () => void;
}

const initContext: Context = {
  token: null,
  account: null,
  isLoggedIn: false,
  register: async () => {},
  login: async () => {},
  loginWithGoogle: async () => {},
  logout: () => {},
};

const AuthContext = createContext(initContext);
const { Provider } = AuthContext;

export const useAuth = () => useContext(AuthContext);

const extractAuthPayload = (data: any): { user: Account | null; token: string | null; loginResponse: any } => {
  const payload = data?.data && !data?.user ? data.data : data;
  const user = payload?.user || payload || null;
  const token = payload?.token || data?.token || null;
  const loginResponse = payload?.user ? payload : { ...payload, user, token };
  return { user, token, loginResponse };
};

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const [token, setToken] = useState<string | null>(
    localStorage.getItem('token') || initContext.token
  );
  const [account, setAccount] = useState<Account | null>(() => {
    const storedAccount = localStorage.getItem('account');
    return storedAccount ? JSON.parse(storedAccount) : initContext.account;
  });
  const [isLoggedIn, setIsLoggedIn] = useState(
    localStorage.getItem('token') ? true : false
  );

  // Registration: capture and store complete response data
  const register = (formData: FormData) => {
    return new Promise((resolve, reject) => {
      axios
        .post('/v1/api/register', formData)
        .then(({ data }) => {
          const { user: accountData, token: accessToken, loginResponse } = extractAuthPayload(data);
          setAccount(accountData);
          setToken(accessToken);
          setIsLoggedIn(true);
          if (accessToken) localStorage.setItem('token', accessToken);
          localStorage.setItem('account', JSON.stringify(accountData));
          localStorage.setItem('loginResponse', JSON.stringify(loginResponse));
          resolve(true);
        })
        .catch((error) => {
          reject(error?.response?.data?.message || error.message);
        });
    });
  };

  // Login: stores the FULL user object so all fields (faculty, department, etc.) are present in context/localStorage
  const login = (username: string, password: string) => {
    return new Promise((resolve, reject) => {
      axios
        .post('/v1/api/login', { email: username, password })
        .then(({ data }) => {
          const { user, token: accessToken, loginResponse } = extractAuthPayload(data);
          if (user?.status !== "ACTIVE") {
            return reject("Account is not active please contact Admin.");
          }
          setAccount(user);
          setToken(accessToken);
          setIsLoggedIn(true);
          if (accessToken) localStorage.setItem('token', accessToken);
          localStorage.setItem('account', JSON.stringify(user));
          localStorage.setItem('loginResponse', JSON.stringify(loginResponse));
          resolve(true);
        })
        .catch((error) => {
          reject(error?.response?.data?.message || error.message);
        });
    });
  };

  const loginWithGoogle = (credential: string) => {
    return new Promise((resolve, reject) => {
      axios
        .post('/v1/api/google-auth', { credential })
        .then(({ data }) => {
          const { user, token: accessToken, loginResponse } = extractAuthPayload(data);
          if (user?.status !== "ACTIVE") {
            return reject("Account is not active please contact Admin.");
          }
          setAccount(user);
          setToken(accessToken);
          setIsLoggedIn(true);
          if (accessToken) localStorage.setItem('token', accessToken);
          localStorage.setItem('account', JSON.stringify(user));
          localStorage.setItem('loginResponse', JSON.stringify(loginResponse));
          resolve(true);
        })
        .catch((error) => {
          reject(error?.response?.data?.message || error.message);
        });
    });
  };

  const logout = () => {
    const currentToken = localStorage.getItem('token');
    if (currentToken) void axios.post('/v1/api/workflow-logout', null, { headers: { Authorization: 'Bearer ' + currentToken } }).catch(() => {});
    setIsLoggedIn(false);
    setAccount(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('account');
    localStorage.removeItem('loginResponse');
  };

  // Optional: re-login with the token already stored.
  const loginWithToken = async () => {
    try {
      const {
        data: { data: accountData, token: accessToken },
      } = await axios.get('/v1/api/login', {
        headers: {
          authorization: `Bearer ${token}`,
        },
      });
      setAccount(accountData);
      setToken(accessToken);
      setIsLoggedIn(true);
      localStorage.setItem('token', accessToken);
      localStorage.setItem('account', JSON.stringify(accountData));
      localStorage.setItem('loginResponse', JSON.stringify({ user: accountData, token: accessToken }));
    } catch (error: any) {
      console.error(error);
      if (error?.response?.statusCode === 401) {
        setToken(null);
        localStorage.removeItem('token');
      }
    }
  };

  useEffect(() => {
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
  }, [token]);

  useEffect(() => {
    if (account) {
      localStorage.setItem('account', JSON.stringify(account));
    } else {
      localStorage.removeItem('account');
    }
  }, [account]);

  useEffect(() => {
    if (!isLoggedIn && !account && token) {
      loginWithToken();
    }
    // eslint-disable-next-line
  }, [isLoggedIn, account, token]);

  const value = useMemo(
    () => ({
      token,
      account,
      isLoggedIn,
      register,
      login,
      loginWithGoogle,
      logout,
    }),
    [token, account, isLoggedIn]
  );

  return <Provider value={value}>{children}</Provider>;
};

export default AuthContext;
